<template>
  <div :class="$style.notesFooter">
    <div v-if="noteState === NoteState.ACTIVE">
      <div :class="$style.notesText" data-test-id="note-text">
        <h3>{{ t('favourites.note.title') }}</h3>
        <p>{{ noteText }}</p>
      </div>
      <div :class="$style.notesActions">
        <div :class="$style.notesActionsLeft">
          <HgButton
            :class="$style.notesButton"
            data-test-id="contact-form-button"
            theme="ghost"
            tag="a"
            target="_blank"
            rel="noopener noreferrer"
            :href="contactFormHref"
            @click.stop="trackContactListerAction"
          >
            <span>
              <HgIcon :icon="Icon.Email" theme="secondary" />
              {{ t('favourites.contactForm.contactAdvertiser') }}
            </span>
          </HgButton>
        </div>
        <div :class="$style.notesActionsRight">
          <HgButton
            :class="$style.notesButton"
            data-test-id="delete-button"
            theme="ghost"
            @click="showDeleteModal"
          >
            <span>
              <HgIcon :icon="Icon.Delete" theme="secondary" />
              {{ t('favourites.note.buttons.deleteNote') }}
            </span>
          </HgButton>
          <HgButton
            :class="$style.notesButton"
            data-test-id="edit-button"
            theme="ghost"
            @click="handleEditAction"
          >
            <span>
              <HgIcon :icon="Icon.Edit" theme="secondary" />
              {{ t('favourites.note.buttons.editNote') }}
            </span>
          </HgButton>
        </div>
      </div>
    </div>

    <div v-if="noteState === NoteState.INACTIVE" :class="$style.notesActions">
      <div :class="$style.notesActionsLeft">
        <HgButton
          :class="$style.notesButton"
          data-test-id="contact-form-button"
          theme="ghost"
          tag="a"
          target="_blank"
          rel="noopener noreferrer"
          :href="contactFormHref"
          @click.stop="trackContactListerAction"
        >
          <span>
            <HgIcon :icon="Icon.Email" theme="secondary" />
            {{ t('favourites.contactForm.contactAdvertiser') }}
          </span>
        </HgButton>
      </div>
      <div :class="$style.notesActionsRight">
        <HgButton
          :class="$style.notesButton"
          data-test-id="write-button"
          theme="ghost"
          @click="handleWriteAction"
        >
          <span>
            <HgIcon :icon="Icon.Edit" theme="secondary" />
            {{ t('favourites.note.buttons.writeNote') }}
          </span>
        </HgButton>
      </div>
    </div>

    <div @click="stopPropagation">
      <HgModal v-model="state.isModalVisible" :fullScreenMobile="true" @close="hideModal">
        <template #title>{{ modalTitle }}</template>
        <template v-if="state.isEditing">
          <HgTextArea
            v-model="state.tempNote.text"
            data-test-id="note-textarea"
            maxlength="500"
            name="user-notes"
          >
          </HgTextArea>
          <div>{{ state.tempNote?.text?.length || 0 }} / 500</div>
        </template>
        <template v-else>
          <p>{{ t('favourites.note.deleteModal.message') }}</p>
        </template>
        <template #footer>
          <HgButton data-test-id="cancel-button" theme="primary-bordered" @click="hideModal">
            {{
              state.isEditing
                ? t('favourites.note.editModal.cancel')
                : t('favourites.note.deleteModal.cancel')
            }}
          </HgButton>
          <HgButton
            v-if="state.isEditing"
            :disabled="isSaveButtonDisabled"
            data-test-id="save-button"
            @click="saveNote"
          >
            {{ t('favourites.note.editModal.save') }}
          </HgButton>
          <HgButton v-else data-test-id="confirm-delete-button" @click="deleteNote">
            {{ t('favourites.note.deleteModal.delete') }}
          </HgButton>
        </template>
      </HgModal>
    </div>
  </div>
</template>

<script setup lang="ts">
import { HgButton, HgIcon, HgModal, HgTextArea, Icon } from '@smg-real-estate/domus';
import { computed, reactive, watch } from 'vue';
import { useStore } from 'vuex';

import { getNote, removeNote, setNote } from '@/components/Note/Note'; // Import note handling methods
import {
  trackContactClick,
  trackNoteAdd,
  trackNoteDeleteTrial,
  trackNoteEdit,
  trackNoteRemove,
  trackNoteSave,
} from '@/components/Note/Note.tracking'; // Import Note tracking actions
import type { FavouritesListItem } from '@/types';

import { useI18n } from 'vue-i18n';
import { type Note } from '../Note';

// Props
const props = defineProps<{
  listingId: string;
  detailPageRoute: string;
  listingPosition: number;
}>();

// Helper functions
function deepCopy(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}

const createNote = (listingId: string, text = ''): Note => ({
  listingId,
  text,
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

// Define the NoteState enum
enum NoteState {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

// Composables
const { t } = useI18n();
const store = useStore();

// State
const state = reactive({
  isModalVisible: false,
  isEditing: false,
  note: null as Note | null,
  tempNote: createNote(props.listingId) as Note,
});

// Computed properties
const modalTitle = computed(() => {
  return state.isEditing
    ? t('favourites.note.editModal.title')
    : t('favourites.note.deleteModal.title');
});

const isSaveButtonDisabled = computed(() => {
  return !state.tempNote.text || state.tempNote.text.trim().length === 0;
});

const noteState = computed(() => {
  return state.note && state.note.text ? NoteState.ACTIVE : NoteState.INACTIVE;
});

const noteText = computed(() => {
  const persistedListing = store.getters.persistedNoteByListingId(props.listingId);
  return persistedListing ? persistedListing.text : state.note?.text;
});

const contactFormHref = computed(() => {
  const url = new URL(props.detailPageRoute, window.location.origin);
  url.searchParams.set('contactForm', 'modal');
  return url.toString();
});

// Watchers
watch(
  () => props.listingId,
  (newListingId) => {
    if (store.getters.isUserAuthenticated) {
      const persistedNote: FavouritesListItem =
        store.getters.persistedNoteByListingId(newListingId);
      state.note = persistedNote
        ? createNote(persistedNote.itemId, persistedNote.text || '')
        : null;
    } else {
      state.note = getNote(newListingId) || null;
    }

    state.tempNote = state.note ? deepCopy(state.note) : createNote(newListingId);
  },
  { immediate: true },
);

// Methods
function showModal(event: Event) {
  event.stopPropagation();
  event.preventDefault();
  state.isEditing = true;
  state.tempNote = state.note ? deepCopy(state.note) : createNote(props.listingId);
  state.isModalVisible = true;
}

function showDeleteModal(event: Event) {
  event.stopPropagation();
  event.preventDefault();
  state.isEditing = false;
  state.isModalVisible = true;
  trackNoteDeleteTrial();
}

function hideModal(event?: Event) {
  if (event) event.preventDefault();
  state.isModalVisible = false;
}

async function saveNote() {
  if (!state.note) {
    state.note = createNote(props.listingId, state.tempNote.text);
    trackNoteSave('add');
  } else {
    state.note.text = state.tempNote.text;
    state.note.updatedAt = Date.now();
    trackNoteSave('edit');
  }

  if (store.getters.isUserAuthenticated) {
    const listId = store.getters.favouritesListId;
    const payload = {
      itemId: state.note.listingId,
      text: state.note.text,
      listId,
    };
    await store.dispatch('persistNoteForAuthUser', payload);
  } else {
    setNote(props.listingId, state.note.text);
  }
  hideModal();
}

async function deleteNote() {
  if (store.getters.isUserAuthenticated) {
    const listId = store.getters.favouritesListId;
    await store.dispatch('persistNoteForAuthUser', {
      listId,
      itemId: state.note!.listingId,
      text: '',
    });
  } else {
    removeNote(props.listingId);
  }
  state.note = null;
  trackNoteRemove();
  hideModal();
}

function stopPropagation(event: Event) {
  event.stopPropagation();
  event.preventDefault();
}

function handleWriteAction(event: Event) {
  showModal(event);
  trackNoteAdd();
}

function handleEditAction(event: Event) {
  showModal(event);
  trackNoteEdit();
}

function trackContactListerAction() {
  trackContactClick({
    listingId: props.listingId,
    listingPosition: props.listingPosition,
  });
}
</script>

<style lang="scss" module>
@import '../../../assets/variables';

.notesFooter {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  border-top: 1px solid #c8c8c8;

  .notesText {
    box-sizing: border-box;
    width: 100%;
    padding: 0 24px;
    border-bottom: 1px solid #c8c8c8;

    h3 {
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      text-align: left;
    }

    p {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      color: #111;
      text-align: left;
    }
  }

  .notesActions {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 16px;

    .notesActionsLeft,
    .notesActionsRight {
      display: flex;
      align-items: center;
    }

    .notesActionsRight {
      margin-left: auto;
    }

    .notesButton {
      span {
        display: flex;
        align-items: center;
        line-height: 16px;

        svg {
          margin-right: 0.5rem;
        }
      }
    }
  }
}

@media (max-width: $viewport-medium-min) {
  .notesFooter {
    .notesActions {
      display: flex;
      flex-direction: column;
      align-items: start;

      .notesActionsRight {
        display: flex;
        flex-direction: column-reverse;
        align-items: start;
        margin-left: 0;
      }
    }
  }
}
</style>
